import { type ReactNode } from "react";
import { PAGE_BANNERS_SLOT_ID } from "@/ui/PageBanner";

interface PageHeaderProps {
  title: string;
  description?: ReactNode;
  /** Pequeno texto exibido acima do título (ex.: módulo/etapa/contexto) */
  eyebrow?: string;
  children?: ReactNode;
  /**
   * `2` quando a tela entra embutida numa página que já tem o próprio
   * `PageHeader` (ex.: as abas de Frota & Equipe): o título vira `h2` e o
   * slot de banners não se repete, porque os banners da tela embutida vão ao
   * slot da página que a recebe. Padrão `1`, o título da página.
   */
  nivel?: 1 | 2;
}

/**
 * Regra global de UI: o PageHeader é SEMPRE o primeiro elemento visual da tela.
 * Banners (avisos, homologação, alertas, info) devem ser renderizados via
 * `<PageBanner>`, que portal-ia para o slot abaixo do header.
 *
 * O título sai em `<h1>`, não em `<h2>`: é o título DA PÁGINA, e cada tela tem
 * exatamente um. Saía como `<h2>` e deixava toda tela sem `h1` — achado
 * TEG-PAD-010 do pacote de QA, que aparece em várias telas do bloco
 * Administração › Parametrização porque a causa é única, aqui. Nenhuma tela
 * combina este cabeçalho com `DetailHeader` (que também emite `h1`), então a
 * troca não cria dois `h1` na mesma página.
 */
export function PageHeader({ title, description, eyebrow, children, nivel = 1 }: PageHeaderProps) {
  const embutido = nivel === 2;
  return (
    <>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-6">
        <div className="min-w-0">
          {eyebrow && (
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
              {eyebrow}
            </p>
          )}
          {/*
            `h1`, não `h2`: este é o título da página, e a casca das áreas não
            emite cabeçalho nenhum acima dele (nem CascaDeArea nem BarraLateral
            têm h1/h2). Emitindo h2, quem navega por cabeçalhos entrava na tela
            sem nível raiz — a árvore começava no nível 2 em todas as 115 telas
            que usam este componente. Par disto: `ui/ErrorState.tsx` emite h2,
            porque lá o texto é um estado dentro da página, não o título dela.
          */}
          {embutido ? (
            <h2 className="text-xl font-semibold tracking-tight min-w-0">{title}</h2>
          ) : (
            <h1 className="text-3xl font-bold tracking-tight min-w-0">{title}</h1>
          )}
          {description && (
            typeof description === "string"
              ? <p className="text-muted-foreground text-sm">{description}</p>
              : <div className="text-muted-foreground text-sm">{description}</div>
          )}
        </div>
        {children && (
          <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto lg:shrink-0 lg:justify-end">
            {children}
          </div>
        )}
      </div>
      {/* Slot global para <PageBanner /> — banners SEMPRE abaixo do título. */}
      {!embutido && (
        <div id={PAGE_BANNERS_SLOT_ID} className="empty:hidden flex flex-col gap-3 mb-4" />
      )}
    </>
  );
}

